import type { IHasDirtyWatcher } from "@frui.ts/dirtycheck";
import { attachAutomaticDirtyWatcher, resetDirty } from "@frui.ts/dirtycheck";
import type { NavigationPath } from "@frui.ts/screens/dist";
import { isNavigationParent, Router } from "@frui.ts/screens/dist";
import ConductorOneChildActive from "@frui.ts/screens/dist/structure/conductorOneChildActive";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import type { IHasValidation } from "@frui.ts/validation";
import { attachAutomaticValidator, hasVisibleErrors, hideValidationErrors, validate } from "@frui.ts/validation";
import { Modules } from "config";
import type ExemptionsRequest from "entities/exemptionsRequest";
import { ExemptionRequestValidationRules } from "entities/exemptionsRequest";
import SearchRequest from "entities/filters/searchRequest";
import { action, computed, isObservableArray, observable, toJS } from "mobx";
import EventBus, {
  vehiclePlatesChangeChanged,
  exemptionCreated,
  licensePlateChangeChanged,
  exemptionUpdated,
  totalCountCharges,
  totalCountExemptions,
  totalCountMessages,
  exemptionCancel,
  exemptionDelete,
  totalCountVehicleRegister,
  vehicleSearchStatus,
  vehicleSynchronizationFetchFlag,
} from "services/eventBus";
import LocalizationService from "services/localizationService";
import { isFilterEmpty, serializeFilters } from "utils/filters";
import type CountrySelectViewModel from "../../countrySelectViewModel";
import ChargesViewModel from "./charges/chargesViewModel";
import ExemptionsViewModel from "./exemptions/exemptionsViewModel";
import type SearchChargesRequest from "entities/searchChargesRequest";
import { SearchChargesRequestValidationRules } from "entities/searchChargesRequest";
import EmailMessageViewModel from "./emailMessage/emailMessageViewModel";
import type SentEmailMessageSearchRequest from "entities/sentEmailMessageSearchRequest";
import { SentEmailMessageRequestValidationRules } from "entities/sentEmailMessageSearchRequest";
import VehicleRegisterViewModel from "./vehicleRegister/vehicleRegisterViewModel";
import type SearchVehicleRegisterVehiclesRequest from "entities/searchVehicleRegisterVehiclesRequest";
import { SearchVehicleRegisterVehiclesRequestValidationRules } from "entities/searchVehicleRegisterVehiclesRequest";
import VehicleRegisterVehicleSearchStatus from "entities/vehicleRegisterVehicleSearchStatus";
import SecurityService from "services/securityService";

type OmitValidationAndDirtyWatcher<T> = Omit<T, keyof IHasDirtyWatcher<T> | keyof IHasValidation<T>>;

@Router.registerRoute({
  route: "",
  name: Router.Self,
  children: [ChargesViewModel, ExemptionsViewModel, EmailMessageViewModel, VehicleRegisterViewModel],
})
export default class SearchViewModel extends ConductorOneChildActive<
  ChargesViewModel | ExemptionsViewModel | EmailMessageViewModel | VehicleRegisterViewModel
> {
  private unsubscribtions: Array<() => void> = [];
  readonly countrySelectViewModel: CountrySelectViewModel;
  @observable filter: SearchRequest & IHasDirtyWatcher<SearchRequest> & Partial<IHasValidation<SearchRequest>>;
  @observable.shallow appliedFilter: OmitValidationAndDirtyWatcher<SearchRequest>;
  @observable totalCountCharges = 0;
  @observable totalCountExemptions = 0;
  @observable totalCountMessages = 0;
  @observable totalCountVehicleRegister = 0;
  @observable itemsCount = 0;
  @observable vehicleSearchStatus: VehicleRegisterVehicleSearchStatus | null;

  constructor(
    private localizationService: LocalizationService,
    private chargesViewModel: ChargesViewModel,
    private securityService: SecurityService,
    private exemptionsViewModel: ExemptionsViewModel,
    private emailMessageViewModel: EmailMessageViewModel,
    private vehicleRegisterViewModel: VehicleRegisterViewModel,
    private eventBus: EventBus,
    private countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>
  ) {
    super();
    this.children.push(this.chargesViewModel, this.exemptionsViewModel, this.emailMessageViewModel);
    if (this.securityService.allowTo("callcenter")) {
      this.children.push(this.vehicleRegisterViewModel);
    }
    this.navigationName = "";
    this.countrySelectViewModel = this.countrySelectViewModelFactory("id", true);
    this.initFilter();
  }

  protected onInitialize(): Promise<any> | void {
    this.unsubscribtions.push(
      this.eventBus.subscribe(vehiclePlatesChangeChanged, this.onVehiclePlateChanged),
      this.eventBus.subscribe(licensePlateChangeChanged, this.onLicensePlateChanged),
      this.eventBus.subscribe(exemptionCreated, this.onExemptionCreated),
      this.eventBus.subscribe(exemptionUpdated, this.onExemptionUpdated),
      this.eventBus.subscribe(exemptionCancel, this.onExemptionCancelled),
      this.eventBus.subscribe(exemptionDelete, this.onExemptionDeleted),
      this.eventBus.subscribe(totalCountExemptions, this.onTotalCountExemptionsUpdate),
      this.eventBus.subscribe(totalCountMessages, this.onTotalCountMessagesUpdate),
      this.eventBus.subscribe(totalCountCharges, this.onTotalCountChargesUpdate),
      this.eventBus.subscribe(totalCountVehicleRegister, this.onTotalCountVehicleRegisterUpdate),
      this.eventBus.subscribe(vehicleSearchStatus, this.onVehicleSearchStatusChanged),
      this.eventBus.subscribe(vehicleSynchronizationFetchFlag, this.onVehicleSynchronizationFetchFlag)
    );
    if (!this.activeChild) {
      this.tryActivateChild(this.chargesViewModel);
    }

    return super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean): Promise<void> {
    if (isClosing) {
      this.unsubscribtions.forEach(unsubsribe => unsubsribe());
      this.unsubscribtions = [];
    }

    return super.onDeactivate(isClosing);
  }

  closeChild(child: ScreenBase) {
    return false;
  }

  protected initFilter() {
    if (!this.filter) {
      const filter = this.createFilter();
      this.resetFilterValues(filter);
      this.filter = attachAutomaticDirtyWatcher(filter, true, { exclude: ["__validation"] });
    }
  }

  protected resetFilterValues(filter: SearchRequest) {
    for (const property in filter) {
      const attr = property as keyof SearchRequest;

      if (typeof attr === "string" && !attr.startsWith("_")) {
        filter[attr] = undefined as any;
      }
    }
    filter._validSince = undefined;
    filter._validUntil = undefined;

    this.totalCountCharges = 0;
    this.totalCountMessages = 0;
    this.totalCountExemptions = 0;
    this.totalCountVehicleRegister = 0;
  }

  protected createFilter() {
    return attachAutomaticValidator(new SearchRequest(), {});
  }

  protected cloneFilterForApply(filter: SearchRequest): OmitValidationAndDirtyWatcher<SearchRequest> {
    const { __dirtycheck, __validation, ...clonedFilter } = filter as SearchRequest &
      IHasDirtyWatcher<SearchRequest> &
      Partial<IHasValidation<SearchRequest>>;

    Object.entries(clonedFilter).forEach(([key, value]) => {
      if (isObservableArray(value)) {
        clonedFilter[key as keyof OmitValidationAndDirtyWatcher<SearchRequest>] = value.slice() as any;
      }
    });

    return clonedFilter;
  }

  async navigate(subPath: string | undefined, params: any): Promise<void> {
    if (params.licensePlate) {
      this.filter.licensePlate = params.licensePlate;
    }

    if (params.officialRecordId) {
      this.filter.officialRecordId = params.officialRecordId;
    }

    if (params.countryId) {
      this.filter.countryId = params.countryId;
    }

    if (params.emailAddress) {
      this.filter.emailAddress = params.emailAddress;
    }

    if (params.orderIdent) {
      this.filter.orderIdent = params.orderIdent;
    }

    if (!isFilterEmpty(this.filter)) {
      this.applyFilter();
    }

    await super.navigate(subPath, params);
  }

  async tryActivateChild(child: ChargesViewModel | ExemptionsViewModel | EmailMessageViewModel): Promise<any> {
    const hasAlreadyActivateChild = this.activeChild !== undefined;
    this.reattachSearchValidator(child);
    await super.tryActivateChild(child);
    if (hasAlreadyActivateChild) {
      this.search();
    }
  }

  getNavigationPath(): NavigationPath {
    let navigationPath;
    if (this.parent && isNavigationParent(this.parent)) {
      navigationPath = this.parent.getChildNavigationPath(this, this.navigationParams);
    } else {
      navigationPath = { path: this.navigationName, params: this.navigationParams, isClosed: false };
    }

    return {
      params: this.navigationParams,
      path: navigationPath.path,
      isClosed: false,
    };
  }

  @computed
  get busyWatcher() {
    return this.activeChild?.busyWatcher;
  }

  @computed
  get isFilterEmpty() {
    return isFilterEmpty(this.appliedFilter);
  }

  get navigationParams() {
    return {
      ...this.navigationParamsValue,
      ...serializeFilters(toJS(this.appliedFilter), false),
    };
  }

  @computed
  get displayVehicleRegisterWarning() {
    return this.vehicleSearchStatus !== VehicleRegisterVehicleSearchStatus.SearchSuccessful;
  }

  @action.bound
  onVehicleSearchStatusChanged(event: ReturnType<typeof vehicleSearchStatus>) {
    this.vehicleSearchStatus = event.payload.vehicleSearchStatus;
  }

  @action.bound
  onVehicleSynchronizationFetchFlag(event: ReturnType<typeof vehicleSynchronizationFetchFlag>) {
    if (event.payload.fetchSearch) {
      this.applyFilterAndLoad();
    }
  }

  @action.bound
  applyFilterAndLoad() {
    if (this.applyFilter()) {
      return this.loadData();
    }
  }

  @action.bound
  applyFilter() {
    if (!validate(this.filter)) {
      return false;
    }

    this.appliedFilter = this.cloneFilterForApply(this.filter);
    resetDirty(this.filter);
    return true;
  }

  @action.bound
  resetFilter() {
    this.resetFilterValues(this.filter);
    resetDirty(this.filter);
  }

  @action.bound
  resetFilterAndLoad() {
    this.resetFilter();
    this.search();
    hideValidationErrors(this.filter);
  }

  @action.bound
  loadData() {
    this.search();
  }

  @action.bound
  private reattachSearchValidator(
    child: ChargesViewModel | ExemptionsViewModel | EmailMessageViewModel | VehicleRegisterViewModel
  ) {
    let validationRules = SentEmailMessageRequestValidationRules;

    if (child instanceof ExemptionsViewModel) {
      validationRules = ExemptionRequestValidationRules;
    }
    if (child instanceof ChargesViewModel) {
      validationRules = SearchChargesRequestValidationRules;
    }
    if (child instanceof VehicleRegisterViewModel) {
      validationRules = SearchVehicleRegisterVehiclesRequestValidationRules;
    }

    attachAutomaticValidator(this.filter, validationRules, hasVisibleErrors(this.filter));
  }

  @action.bound
  private onLicensePlateChanged(event: ReturnType<typeof licensePlateChangeChanged>) {
    this.resetFilter();

    this.applyFilterAndLoad();
  }

  @action.bound
  private onVehiclePlateChanged(event: ReturnType<typeof vehiclePlatesChangeChanged>) {
    this.resetFilter();
    this.filter.licensePlate = event.payload.entity.newLicensePlate;
    this.filter.countryId = event.payload.entity.newCountryId;

    if (event.payload.exemptions.length > 0 && event.payload.charges.length === 0) {
      this.tryActivateChild(this.exemptionsViewModel);
    } else if (event.payload.exemptions.length === 0 && event.payload.charges.length > 0) {
      this.tryActivateChild(this.chargesViewModel);
    }

    this.applyFilterAndLoad();
  }

  @action.bound
  private onExemptionCreated(event: ReturnType<typeof exemptionCreated>) {
    const exemption = event.payload.entity;
    if (this.filter.licensePlate !== exemption.vehicle.licensePlate || this.filter.countryId !== exemption.vehicle.countryId) {
      this.resetFilter();
      this.filter.licensePlate = exemption.vehicle.licensePlate;
      this.filter.countryId = exemption.vehicle.countryId;
    } else if (!!this.filter.officialRecordId && this.filter.officialRecordId !== exemption.officialRecordId) {
      this.resetFilter();
      this.filter.officialRecordId = exemption.officialRecordId;
    }

    this.applyFilterAndLoad();
  }

  @action.bound
  private onExemptionCancelled(event: ReturnType<typeof exemptionCancel>) {
    this.applyFilterAndLoad();
  }

  @action.bound
  private onExemptionDeleted(event: ReturnType<typeof exemptionDelete>) {
    if (this.activeChild instanceof ExemptionsViewModel) {
      this.activeChild.items = this.activeChild.items.filter(x => x.exemptionId !== event.payload.exemptionId);
      this.itemsCount = this.totalCountExemptions = this.activeChild.items.length;
    } else {
      this.applyFilterAndLoad();
    }
  }

  @action.bound
  private onExemptionUpdated(event: ReturnType<typeof exemptionUpdated>) {
    const exemption = event.payload.entity;
    if (this.filter.licensePlate !== exemption.vehicle.licensePlate || this.filter.countryId !== exemption.vehicle.countryId) {
      this.resetFilter();
      this.filter.licensePlate = exemption.vehicle.licensePlate;
      this.filter.countryId = exemption.vehicle.countryId;
    } else if (!!this.filter.officialRecordId && this.filter.officialRecordId !== exemption.creationRecordId) {
      this.resetFilter();
      this.filter.officialRecordId = exemption.creationRecordId;
    }

    this.applyFilterAndLoad();
  }

  @action.bound
  private onTotalCountExemptionsUpdate(event: ReturnType<typeof totalCountExemptions>) {
    this.totalCountExemptions = event.payload.totalCountExemptions ?? 0;
    this.getItemsCount();
  }

  @action.bound
  private onTotalCountMessagesUpdate(event: ReturnType<typeof totalCountMessages>) {
    this.totalCountMessages = event.payload.totalCountMessages ?? 0;
    this.getItemsCount();
  }

  @action.bound
  private onTotalCountChargesUpdate(event: ReturnType<typeof totalCountCharges>) {
    this.totalCountCharges = event.payload.totalCountCharges ?? 0;
    this.getItemsCount();
  }

  @action.bound
  private onTotalCountVehicleRegisterUpdate(event: ReturnType<typeof totalCountVehicleRegister>) {
    this.totalCountVehicleRegister = event.payload.totalCountVehicleRegister ?? 0;
    this.getItemsCount();
  }

  disableInput(
    attribute: keyof SearchChargesRequest | keyof ExemptionsRequest | keyof EmailMessageViewModel | keyof VehicleRegisterViewModel
  ) {
    if (this.activeChild instanceof ExemptionsViewModel) {
      switch (attribute) {
        case "emailAddress":
        case "notificationPhoneNumber":
        case "orderIdent":
        case "bankAccountNumber":
        case "bankCode":
        case "cardNumber":
        case "chargeOrderId":
        case "chargeId":
          return true;

        default:
          return false;
      }
    }
    if (this.activeChild instanceof EmailMessageViewModel) {
      switch (attribute) {
        case "notificationPhoneNumber":
        case "bankAccountNumber":
        case "officialRecordId":
        case "bankCode":
        case "cardNumber":
          return true;

        default:
          return false;
      }
    }
    if (this.activeChild instanceof VehicleRegisterViewModel) {
      switch (attribute) {
        case "emailAddress":
        case "notificationPhoneNumber":
        case "orderIdent":
        case "bankAccountNumber":
        case "bankCode":
        case "cardNumber":
        case "chargeOrderId":
        case "officialRecordId":
        case "chargeId":
          return true;

        default:
          return false;
      }
    } else {
      return false;
    }
  }

  getItemsCount(): void {
    if (this.activeChild instanceof ExemptionsViewModel) {
      this.itemsCount = this.totalCountExemptions;
    } else if (this.activeChild instanceof ChargesViewModel) {
      this.itemsCount = this.totalCountCharges;
    } else if (this.activeChild instanceof EmailMessageViewModel) {
      this.itemsCount = this.totalCountMessages;
    } else if (this.activeChild instanceof VehicleRegisterViewModel) {
      this.itemsCount = this.totalCountVehicleRegister;
    } else {
      this.itemsCount = 0;
    }
  }

  search() {
    if (!this.activeChild) {
      return;
    }

    const clonedFilters = this.cloneFilterForApply(this.appliedFilter);
    const filtersForApply = this.getFiltersForApply(clonedFilters);

    if (validate(this.filter) && filtersForApply !== undefined) {
      this.activeChild.search(filtersForApply);
    } else {
      this.activeChild.resetItems();
    }
  }

  getFiltersForApply(
    clonedFilters: any
  ): ExemptionsRequest | SearchChargesRequest | SentEmailMessageSearchRequest | SearchVehicleRegisterVehiclesRequest | undefined {
    if (this.activeChild instanceof ExemptionsViewModel) {
      this.itemsCount = this.totalCountExemptions;
    } else if (this.activeChild instanceof ChargesViewModel) {
      this.itemsCount = this.totalCountCharges;
    } else if (this.activeChild instanceof EmailMessageViewModel) {
      this.itemsCount = this.totalCountMessages;
    } else if (this.activeChild instanceof VehicleRegisterViewModel) {
      this.itemsCount = this.totalCountVehicleRegister;
    } else {
      return undefined;
    }

    return { ...clonedFilters };
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.callCenter, code, size);
  }

  translateAttribute(attribute: keyof SearchChargesRequest | keyof ExemptionsRequest, size?: number) {
    return this.localizationService.translateAttribute("chargesRequest", attribute, size);
  }

  translateVehicleSearchStatus(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.callCenter, `vehicleRegister.${code}`, size);
  }
}
